import {createReducer} from '@reduxjs/toolkit';

import {
  ENHANCEMENT_MODE,
  IMG_FILTERS,
  PROCESS_MAP,
  SPECTRAL_INDICES_KEY,
  TILE_MODE,
  TOURS,
  UI_MODES,
} from '../constants/index.js';
import {clearActiveFolder} from '../actions/folders.js';
import {get as getStored} from '../util/store.js';
import {resetTour, setTour} from '../actions/tour.js';
import {
  setColorEnhanceMode,
  setImageFilter,
  setProcess,
  setProcessColor,
  setTileMode,
} from '@explorer/actions/tiles';
import {setMode as setUIMode} from '../actions/ui.js';

const defaultSettings = {
  // process color, such as an index (NDVI/NDWI)
  process: null,
  // the current color ramp
  processColor: null,
  // toggle the automatic image enhancement.
  enhanceMode: ENHANCEMENT_MODE.OFF,
  // slider values,
  enhanceFilters: {
    [IMG_FILTERS.BRIGHTNESS]: 0,
    [IMG_FILTERS.CONTRAST]: 0,
    [IMG_FILTERS.SATURATION]: 0,
  },
};

export const initialState = {
  // sets which tiles layer is being displayed.
  mode: TILE_MODE.BASEMAPS,

  modeSettings: {
    [TILE_MODE.BASEMAPS]: defaultSettings,
    [TILE_MODE.FOLDERS]: defaultSettings,
    // results does not support indices or enhance
    [TILE_MODE.RESULTS]: defaultSettings,
  },
};

const tiles = createReducer(initialState, builder => {
  builder.addCase(setTileMode, (state, {payload}) => {
    state.mode = payload;
  });

  builder.addCase(setProcessColor, (state, {payload}) => {
    state.modeSettings[state.mode] = {
      ...state.modeSettings[state.mode],
      processColor: payload,
    };
  });

  builder.addCase(setColorEnhanceMode, (state, {payload}) => {
    const nextSettings = {
      ...state.modeSettings[state.mode],
      enhanceMode: payload.mode ?? ENHANCEMENT_MODE.OFF,
      process:
        payload.mode && payload.mode !== ENHANCEMENT_MODE.OFF
          ? null
          : state.modeSettings[state.mode].process,
    };

    if (payload.options && payload.options.resetFilters === true) {
      nextSettings.enhanceFilters = {
        [IMG_FILTERS.BRIGHTNESS]: 0,
        [IMG_FILTERS.CONTRAST]: 0,
        [IMG_FILTERS.SATURATION]: 0,
      };
    }

    state.modeSettings[state.mode] = nextSettings;
  });

  builder.addCase(setImageFilter, (state, {payload}) => {
    state.modeSettings[state.mode].enhanceFilters[payload.filterName] =
      payload.value;
  });

  builder.addCase(setProcess, (state, {payload: process}) => {
    const processColor = getStored(SPECTRAL_INDICES_KEY)
      ? getStored(SPECTRAL_INDICES_KEY)[process]
      : '';

    state.modeSettings[state.mode] = {
      ...state.modeSettings[state.mode],
      process,
      processColor,
      enhanceMode: ENHANCEMENT_MODE.OFF,
    };
  });

  builder.addCase(clearActiveFolder, state => {
    state.modeSettings[TILE_MODE.FOLDERS] = {
      ...state.modeSettings[TILE_MODE.FOLDERS],
      enhanceMode: ENHANCEMENT_MODE.OFF,
      process:
        state.modeSettings[TILE_MODE.FOLDERS].process !== null
          ? PROCESS_MAP.RGB
          : null,
    };
  });

  builder.addCase(setTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return initialState;
    }
    return state;
  });

  builder.addCase(resetTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return initialState;
    }
    return state;
  });

  builder.addCase(setUIMode, (state, {payload}) => {
    if (payload === UI_MODES.COMPARE) {
      state.modeSettings[state.mode] = {
        ...state.modeSettings[state.mode],
        enhanceMode: ENHANCEMENT_MODE.OFF,
      };
    }
  });
});

export default tiles;
